"use client";

/**
 * `useDirtyConfirm` — wires the browser's `beforeunload` confirmation
 * prompt while a form has unsaved changes.
 *
 * The hook installs a `beforeunload` listener on the `window` whenever
 * `dirty` is `true`, and removes it when `dirty` flips back to `false`
 * (or the component unmounts). The handler calls `e.preventDefault()`
 * and assigns a non-empty string to `e.returnValue` — both are required
 * by modern browsers to actually trigger the native confirmation prompt
 * when the user closes the tab, navigates away, or refreshes.
 *
 * ## Why only beforeunload, not Next.js router interception
 *
 * Next.js 15's App Router does **not** expose a `routeChangeStart`
 * event analogous to the Pages Router's `router.events`. The
 * `useRouter` hook returns a `push`/`replace`/`back` API but no
 * subscription point. Detecting client-side navigation therefore
 * requires either:
 *
 * 1. Patching `router.push` (fragile — relies on internal mutability),
 * 2. Using `usePathname()` + `useSearchParams()` to detect a change
 *    after it has already happened (too late to cancel), or
 * 3. Wrapping every `<Link>` and call site to gate navigation manually.
 *
 * Approach 3 is what callers should do for full SPA-navigation
 * coverage: pair this hook with a confirm in their `<Link onClick>` or
 * router-push wrapper. The pragmatic v0.1 surface here is the reliable
 * `beforeunload` half — it covers tab close, refresh, and external
 * link clicks, which is the catastrophic-data-loss case. SPA-internal
 * navigation can still discard a draft, but autosave (Task 17) cushions
 * that path.
 *
 * Plan UI-5 will revisit this with either a router-event shim or a
 * platform-level navigation guard.
 *
 * ## Implementation notes
 *
 * - The effect's dependency is `dirty`. When it flips, the effect's
 *   cleanup runs (removing the previous listener) and the new listener
 *   is registered or omitted based on the new value. There is no
 *   listener to remove when `dirty` was already `false`, so the cleanup
 *   is conditional.
 * - The handler is defined inside the effect so each registration uses
 *   a fresh function reference — the cleanup's `removeEventListener`
 *   then has the exact reference to remove.
 * - `e.returnValue = ""` would be ignored by Chrome (which requires a
 *   truthy string); we use a non-empty placeholder. Modern browsers
 *   override the message regardless, so the literal string is never
 *   shown.
 */

import { useEffect } from "react";

export interface UseDirtyConfirmOptions {
  /**
   * Whether the form currently holds unsaved changes. Typically wired
   * from `useForm().dirty`. When `true`, the browser will prompt before
   * unload.
   */
  dirty: boolean;
}

/**
 * Registers a `beforeunload` confirmation while `dirty` is `true`.
 * Returns nothing — the side effect is the listener registration.
 */
export function useDirtyConfirm({ dirty }: UseDirtyConfirmOptions): void {
  useEffect(() => {
    if (!dirty) return;

    // Defining the handler inline (rather than at module scope) gives
    // each effect run a stable reference for the matching
    // removeEventListener call. The browser only calls one of these at
    // a time per `window`, so there's no risk of stale closures here.
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Some browsers (older Chrome, Edge) require a truthy returnValue;
      // modern engines override the message text but still need the
      // assignment to opt into the confirmation flow.
      e.returnValue = "unsaved-changes";
    };

    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [dirty]);
}
